import { AssetType } from '@/modules/asset/enums/AssetType';
import { AiBatchReviewRecordChange } from '@/modules/ai/models/AiBatchReviewRecordChange';
import { Asset } from '@/modules/asset/models/Asset';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { BaseExtractedRecordModel } from '@/modules/ai/models/abstracts/BaseExtractedRecordModel';
import Lang from '@/modules/core/app/services/Lang';

export class ExtractedAsset extends BaseExtractedRecordModel {
  id?: number;
  documentExtractionId?: number;
  assetId?: number | null;
  asset?: Asset | null;
  name?: string;
  type?: string;
  estimatedValue?: number;
  currentValue?: number;
  atDeathValue?: number;
  purchasePrice?: number;
  currencyId?: number;
  shareOwnershipType?: string;

  constructor(model: Partial<ExtractedAsset> = {}) {
    super(model);

    this.id = model.id;
    this.documentExtractionId = model.documentExtractionId;
    this.assetId = model.assetId;
    this.name = model.name;
    this.type = model.type;
    this.estimatedValue = model.estimatedValue;
    this.currentValue = model.currentValue;
    this.atDeathValue = model.atDeathValue;
    this.purchasePrice = model.purchasePrice;
    this.currencyId = model.currencyId;
    this.shareOwnershipType = model.shareOwnershipType;
    this.asset = Asset.mapModelOrNull(model.asset);
    this.rememberMatchOnFile();
  }

  getModelName(): ModelType {
    return ModelType.EXTRACTED_ASSET;
  }

  get matchedRecord(): Asset | null {
    return this.asset ?? null;
  }

  protected get matchedRecordKey(): string {
    return 'asset';
  }

  protected get matchedId(): number | null | undefined {
    return this.assetId;
  }

  protected set matchedId(id: number | null | undefined) {
    this.assetId = id;
  }

  get label(): string {
    return this.name ?? '';
  }

  get matchedLabel(): string {
    return this.asset?.name ?? '';
  }

  protected get comparedValues(): (AiBatchReviewRecordChange | null)[] {
    const amount: (value: unknown) => string = (value: unknown): string => this.formatAmount(value);

    return [
      this.changeFor(Lang.t('document.ingestion-review-field-name'), this.asset?.name, this.name),
      this.changeFor(
        Lang.t('asset.current-value'),
        this.asset?.currentValue,
        this.currentValue,
        amount
      ),
      this.changeFor(
        Lang.t('asset.value-at-date-of-death'),
        this.asset?.atDeathValue,
        this.atDeathValue,
        amount
      ),
      this.changeFor(
        Lang.t('asset.estimated-value'),
        this.asset?.estimatedValue,
        this.estimatedValue,
        amount
      ),
      this.changeFor(
        Lang.t('referral.prefilling-inventory-purchase-price'),
        this.asset?.purchasePrice,
        this.purchasePrice,
        amount
      )
    ];
  }

  get showPriceColumn(): boolean {
    return [
      AssetType.REAL_ESTATE.key,
      AssetType.LIFE_INSURANCE.key,
      AssetType.ART_JEWELRY.key,
      AssetType.BELONGING.key,
      AssetType.PRIVATE_STOCK.key,
      AssetType.OTHER.key
    ].includes(this.type as string);
  }

  get priceAmount(): number | undefined {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return this.atDeathValue;
    }

    return this.purchasePrice;
  }

  get formattedPriceAmount(): string {
    return this.formatAmount(this.priceAmount);
  }

  get formattedPriceAmountSubLabel(): string {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return Lang.t('referral.prefilling-inventory-benefit-amount');
    }

    return Lang.t('referral.prefilling-inventory-purchase-price');
  }

  get mainAmount(): number | undefined {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return this.currentValue ?? this.estimatedValue;
    }

    return this.atDeathValue ?? this.currentValue ?? this.estimatedValue;
  }

  get formattedAmount(): string {
    return this.formatAmount(this.mainAmount);
  }

  get formattedAmountSubLabel(): string {
    if (this.type === AssetType.LIFE_INSURANCE.key || this.type === AssetType.CASH.key) {
      return Lang.t('referral.prefilling-inventory-cash-value');
    }

    if (this.type === AssetType.PRIVATE_STOCK.key) {
      return Lang.t('referral.prefilling-inventory-total-value');
    }

    return Lang.t('referral.prefilling-inventory-market-value');
  }
}
